import { useState, useEffect, useRef, useMemo } from "react";
import { get, ref } from "firebase/database";
import { db } from "../../functions/api_firebase";
import BarraSuperior from "./barra_superior";
import AnimacionCarga from "./animacion_de_carga";
import "../css/animacion_de_carga.css";
import { obtenerNits, CONFIG, ultimosMeses, etiquetaMeses } from "../../functions/variables_globales";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineElement,
  LineController,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
} from 'chart.js';
import {
  obtenerUsuarios,
  obtenerReservas,
  obtenerVisitantes,
  obtenerNovedades,
  obtenerRondas,
  obtenerNoticias,
  obtenerEncomiendas,
  obtenerPQRS,
  obtenerAnuncios,
  obtenerNotificaciones,
  obtenerClientes,
  obtenerGPS,
  obtenerEventos,
  obtenerTickets
} from "../../functions/consultas";
import { agregarRazonSocial, crearTitulo, crearGrafica, subirDatos } from "../../functions/gestion";

const MESES_VENTANA = 3;

const Estadistica = () => {
  const [animacionCarga, setCargando] = useState(true);
  const [datosMostrar, setDatosMostrar] = useState(false);
  const [graficaMostrar, setGraficaMostrar] = useState(true);
  const [estadisticas, setEstadisticas] = useState({});
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0 });
  const hasFetched = useRef(false);
  const meses = useMemo(() => ultimosMeses(MESES_VENTANA), []);
  const periodo = useMemo(() => etiquetaMeses(meses), [meses]);
  const mesesPermitidos = useMemo(
    () => new Set(meses.map(({ year, mes }) => `${year}-${mes}`)),
    [meses]
  );
  const inc = (map, key) => key && map.set(key, (map.get(key) || 0) + 1);
  const toObj = (maps) => Object.fromEntries(
    Object.entries(maps).map(([k, map]) => [k, Object.fromEntries(map)])
  );
  const procesadores = {
    reservas: (data) => {
      const m = { zona: new Map(), horarioInicio: new Map(), horarioFinal: new Map(), mes: new Map(), dia: new Map() };
      const añoActual = new Date().getFullYear().toString();
      Object.values(data).forEach(({ fecha, zonaId, horaInicio, horaFinal }) => {
        if (!fecha) return;
        inc(m.zona, zonaId);
        inc(m.horarioInicio, horaInicio);
        inc(m.horarioFinal, horaFinal);
        if (fecha.startsWith(añoActual)) {
          inc(m.mes, fecha.substring(0, 7));
          inc(m.dia, fecha);
        }
      });
      return toObj(m);
    },

    visitantes: (data) => {
      const m = { pase: new Map(), horaIngreso: new Map(), horaSalida: new Map(), mes: new Map(), dia: new Map() };
      Object.values(data).forEach(({ tipoPermiso, fechaCreacionRegistro, fechaHoraIngreso, fechaHoraSalida }) => {
        if (tipoPermiso && tipoPermiso !== "...") {
          inc(m.pase, tipoPermiso);
          if (mesesPermitidos.has(fechaCreacionRegistro?.substring(0, 7))) {
            inc(m.mes, fechaCreacionRegistro.substring(0, 7));
            inc(m.dia, fechaCreacionRegistro.substring(0, 10));
          }
        }
        fechaHoraIngreso && inc(m.horaIngreso, fechaHoraIngreso.substring(11, 13));
        fechaHoraSalida && inc(m.horaSalida, fechaHoraSalida.substring(11, 13));
      });
      return toObj(m);
    },

    encomiendas: (data) => {
      const m = { proveedor: new Map(), dia: new Map(), mes: new Map(), diasEntrega: new Map(), horaIngreso: new Map(), horaEntrega: new Map() };
      Object.values(data).forEach(item => {
        item.quienLoTrae && inc(m.proveedor, item.quienLoTrae.toUpperCase().replace("  ", " "));
        if (mesesPermitidos.has(item.fechaCreacion?.substring(0, 7))) {
          inc(m.dia, item.fechaCreacion.slice(0, 10));
          inc(m.mes, item.fechaCreacion.substring(0, 7));
        }
        
        if (item.estado === "Entregado" && item.fechaCreacion && item.fecha_fin_encomienda) {
          const dias = Math.max(0, Math.round((new Date(item.fecha_fin_encomienda) - new Date(item.fechaCreacion)) / 86400000));
          !isNaN(dias) && inc(m.diasEntrega, dias);
        }
        
        item.hora_inicio_encomienda && inc(m.horaIngreso, item.hora_inicio_encomienda.slice(0, 2));
        item.hora_fin_encomienda && inc(m.horaEntrega, item.hora_fin_encomienda.slice(0, 2));
      });
      const result = toObj(m);
      if (result.proveedor) {
        const topProveedores = Object.entries(result.proveedor)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 20);
        result.proveedor = Object.fromEntries(topProveedores);
      }
      
      return result;
    },

    usuarios: (data) => {
      const m = { permisos: new Map(), copropiedad: new Map(), vivienda: new Map() };
      const dirPorUnidad = new Map();

      Object.values(data).forEach(({ Permisos, CodigoCliente, Direccion }) => {
        inc(m.permisos, Permisos);
        
        if (CodigoCliente) {
          const nombre = agregarRazonSocial(CodigoCliente, "nombre");
          inc(m.copropiedad, nombre);
          
          if (Direccion) {
            if (!dirPorUnidad.has(nombre)) dirPorUnidad.set(nombre, new Set());
            dirPorUnidad.get(nombre).add(Direccion);
          }
        }
        
        Direccion && inc(m.vivienda, Direccion);
      });

      return {
        ...toObj(m),
        viviendasPorCopropiedad: Object.fromEntries(
          Array.from(dirPorUnidad, ([k, v]) => [k, v.size])
        )
      };
    },

    novedades: (data) => {
      const m = { tipo: new Map(), hora: new Map(), mes: new Map(), dia: new Map() };
      Object.values(data).forEach(({ tipoZona, fecha_y_hora }) => {
        inc(m.tipo, tipoZona);
        if (fecha_y_hora) {
          inc(m.hora, fecha_y_hora.substring(11, 13));
          if (mesesPermitidos.has(fecha_y_hora.substring(0, 7))) {
            inc(m.mes, fecha_y_hora.substring(0, 7));
            inc(m.dia, fecha_y_hora.substring(0, 10));
          }
        }
      });
      return toObj(m);
    },

    rondas: (data) => {
      const m = { fecha: new Map(), hora: new Map(), mes: new Map() };
      Object.values(data).forEach(({ fecha, hora }) => {
        inc(m.hora, hora?.substring(0, 2));
        if (mesesPermitidos.has(fecha?.substring(0, 7))) {
          inc(m.fecha, fecha);
          inc(m.mes, fecha.substring(0, 7));
        }
      });
      return toObj(m);
    },

    panicos: (data) => {
      const m = { tipo: new Map(), tiempoAtencion: new Map(), dia: new Map() };
      const añoActual = new Date().getFullYear().toString();
      Object.values(data).forEach(({ tipo, fecha_y_hora_creacion, fecha_y_hora_soporte }) => {
        inc(m.tipo, tipo);
        
        if (fecha_y_hora_creacion) {
          if (fecha_y_hora_creacion.startsWith(añoActual)) {
            inc(m.dia, fecha_y_hora_creacion.substring(0, 10));
          }
          
          const inicio = new Date(fecha_y_hora_creacion);
          const fin = isNaN(new Date(fecha_y_hora_soporte)) ? new Date() : new Date(fecha_y_hora_soporte);
          inc(m.tiempoAtencion, Math.floor((fin - inicio) / 60000) % 60);
        }
      });
      return toObj(m);
    }
  };
  const obtenerPanicos = async () => {
    try {
      const permisos = localStorage.getItem("Permisos");
      const codigoCliente = localStorage.getItem("CodigoCliente");

      if (permisos === "SUPER" || codigoCliente === "900362633") {
        const nits = await obtenerNits();
        const respuestas = await Promise.all(nits.map(nit => get(ref(db, `/sos/${nit}`))));
        return respuestas.reduce((acc, snap) => ({ ...acc, ...snap.val() }), {});
      }
      
      const snap = await get(ref(db, `/sos/${codigoCliente}`));
      return snap.val() || {};
    } catch (error) {
      console.log(error.message);
      return {};
    }
  };
  const capturarDatos = async () => {
    setCargando(true);
    
    try {
      const esSuper = CONFIG.esSuper();

      const fuentes = [
        { key: 'noticias', fn: obtenerNoticias },
        { key: 'visitantes', fn: () => obtenerVisitantes(meses) },
        { key: 'encomiendas', fn: () => obtenerEncomiendas(meses) },
        { key: 'reservas', fn: obtenerReservas },
        { key: 'pqrs', fn: obtenerPQRS },
        { key: 'anuncios', fn: obtenerAnuncios },
        { key: 'notificaciones', fn: obtenerNotificaciones },
        { key: 'novedades', fn: () => obtenerNovedades(meses) },
        { key: 'rondas', fn: () => obtenerRondas(meses) },
        { key: 'eventos', fn: obtenerEventos },
        { key: 'usuarios', fn: obtenerUsuarios },
        { key: 'panicos', fn: obtenerPanicos },
        ...(esSuper
          ? [
              { key: 'gps', fn: obtenerGPS },
              { key: 'tickets', fn: obtenerTickets },
              { key: 'clientes', fn: obtenerClientes }
            ]
          : [])
      ];

      setProgreso({ hechos: 0, total: fuentes.length });
      setCargando(false);

      await Promise.all(fuentes.map(async ({ key, fn }) => {
        try {
          const data = await fn();
          if (!data) return;

          const conteo = Array.isArray(data) ? data.length : Object.keys(data).length;
          const stats = procesadores[key] ? procesadores[key](data) : null;

          setEstadisticas(prev => ({
            ...prev,
            contadores: { ...prev.contadores, [key]: conteo },
            ...(stats ? { [key]: stats } : {})
          }));
        } catch (error) {
          console.error(`Error cargando ${key}:`, error);
        } finally {
          setProgreso(prev => ({ ...prev, hechos: prev.hechos + 1 }));
        }
      }));
    } catch (error) {
      console.error("Error capturando datos:", error);
      setCargando(false);
    }
  };
  const cantidadDatos = useMemo(() => {
    const { contadores = {}, usuarios = {} } = estadisticas;
    const permisos = localStorage.getItem("Permisos");
    const razonSocial = localStorage.getItem("RazonSocial");
    const casas = estadisticas.usuarios
      ? usuarios.viviendasPorCopropiedad?.[razonSocial?.toUpperCase()] || 0
      : undefined;

    const base = [
      { modulo: 'Noticias', cantidad: contadores.noticias },
      { modulo: 'Visitantes', cantidad: contadores.visitantes },
      { modulo: 'Eventos', cantidad: contadores.eventos },
      { modulo: 'Encomiendas', cantidad: contadores.encomiendas },
      { modulo: 'Reservas', cantidad: contadores.reservas },
      { modulo: 'Pqrs', cantidad: contadores.pqrs },
      { modulo: 'Anuncios', cantidad: contadores.anuncios },
      { modulo: 'Notificaciones', cantidad: contadores.notificaciones },
      { modulo: 'Novedades', cantidad: contadores.novedades },
      { modulo: 'Rondas', cantidad: contadores.rondas },
      { modulo: 'Usuarios', cantidad: contadores.usuarios },
      { modulo: 'Casas', cantidad: casas }
    ];

    return permisos === "SUPER" 
      ? [...base, 
          { modulo: 'GPS', cantidad: contadores.gps },
          { modulo: 'Tickets', cantidad: contadores.tickets },
          { modulo: 'Clientes', cantidad: contadores.clientes }
        ]
      : base;
  }, [estadisticas]);
  useEffect(() => {
    Chart.register(
      RadialLinearScale, PointElement, LineElement, LineController,
      Filler, Tooltip, Legend, CategoryScale, LinearScale,
      BarElement, Title, ArcElement, ChartDataLabels
    );
  }, []);
  useEffect(() => {
    if (!hasFetched.current) {
      capturarDatos();
      hasFetched.current = true;
    }
  }, []);

  if (animacionCarga) return <AnimacionCarga />;

  return (
    <div>
      <BarraSuperior />
      <div className="BloqueModulo">

        <div className="estadistica-opciones">
          <div className="estadistica-opciones-fila">
            <h2 className="estadistica-titulo-seccion">Opciones de visualización</h2>
            <label className="estadistica-check">
              <input type="checkbox" checked={graficaMostrar} onChange={(e) => setGraficaMostrar(e.target.checked)} />
              Mostrar Gráfica de datos
            </label>
            <label className="estadistica-check">
              <input type="checkbox" checked={datosMostrar} onChange={(e) => setDatosMostrar(e.target.checked)} />
              Mostrar Tabla de datos
            </label>
          </div>

          <div className="estadistica-opciones-meta">
            <p>Filtro: <strong>{periodo}</strong></p>
            {progreso.total > 0 && progreso.hechos < progreso.total && (
              <p className="estadistica-opciones-cargando">
                Cargando módulos… {progreso.hechos} de {progreso.total}
              </p>
            )}
          </div>
        </div>

        {crearTitulo('Resumen general')}

        <div className="ListadoInmuebles" style={{ marginBottom: 24 }}>
          <table id="table" className="tabla-responsive">
            <thead>
              <tr>
                <th>Módulo</th>
                <th>Datos</th>
              </tr>
            </thead>
            <tbody>
              {cantidadDatos.map((item, index) => (
                <tr key={index}>
                  <td>{item.modulo}</td>
                  <td>{item.cantidad === undefined ? '…' : item.cantidad}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => subirDatos(cantidadDatos)}
            disabled={progreso.total === 0 || progreso.hechos < progreso.total}
          >
            SUBIR DATOS
          </button>
        </div>

        {estadisticas.usuarios && (
          <>
            {crearTitulo('Residentes')}
            {crearGrafica('barra', 'Total de Usuarios registrados por copropiedad', estadisticas.usuarios.copropiedad, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Permisos por usuario', estadisticas.usuarios.permisos, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Cantidad de viviendas registradas por copropiedad', estadisticas.usuarios.viviendasPorCopropiedad, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Detalle de las viviendas registradas por copropiedad', estadisticas.usuarios.vivienda, 600, 1400, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.reservas && (
          <>
            {crearTitulo('Zonas sociales')}
            {crearGrafica('barra', 'zonas sociales más utilizadas', estadisticas.reservas.zona, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Reservas por mes', estadisticas.reservas.mes, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Reservas por día', estadisticas.reservas.dia, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Hora de inicio reservas', estadisticas.reservas.horarioInicio, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Hora de finalización reservas', estadisticas.reservas.horarioFinal, 600, 1400, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.visitantes && (
          <>
            {crearTitulo('Pases de visitantes')}
            {crearGrafica('barra', 'Tipo de pase más generados', estadisticas.visitantes.pase, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Pases por mes', estadisticas.visitantes.mes, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Pases por día', estadisticas.visitantes.dia, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Hora de ingreso', estadisticas.visitantes.horaIngreso, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Hora de salida', estadisticas.visitantes.horaSalida, 600, 1400, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.encomiendas && (
          <>
            {crearTitulo('Encomiendas')}
            {crearGrafica('barra', 'Proveedores de servicio (Top 20)', estadisticas.encomiendas.proveedor, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Paquetes por mes', estadisticas.encomiendas.mes, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Paquetes por día', estadisticas.encomiendas.dia, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Paquetes por hora (Ingreso a portería)', estadisticas.encomiendas.horaIngreso, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Paquetes por hora (Entrega a residente)', estadisticas.encomiendas.horaEntrega, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Días que dura paquete en portería', estadisticas.encomiendas.diasEntrega, 600, 1400, "numero", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.novedades && (
          <>
            {crearTitulo('Reporte de novedades')}
            {crearGrafica('radar', 'Tipo de novedades más reportados', estadisticas.novedades.tipo, 600, 600, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Reporte de novedades por mes', estadisticas.novedades.mes, 400, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Reporte de novedades por día', estadisticas.novedades.dia, 400, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Reporte de novedades por hora', estadisticas.novedades.hora, 400, 1400, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.rondas && (
          <>
            {crearTitulo('Puntos de marcación')}
            {crearGrafica('barra', 'Puntos de marcación por mes', estadisticas.rondas.mes, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Puntos de marcación por día', estadisticas.rondas.fecha, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Puntos de marcación por hora', estadisticas.rondas.hora, 400, 1400, "texto", graficaMostrar, datosMostrar)}
          </>
        )}

        {estadisticas.panicos && (
          <>
            {crearTitulo('Atención de SOS')}
            {crearGrafica('barra', 'Tipo de pánico atendido', estadisticas.panicos.tipo, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Pánicos atendidos por día', estadisticas.panicos.dia, 600, 1400, "texto", graficaMostrar, datosMostrar)}
            {crearGrafica('barra', 'Tiempo de atención en minutos de cada alerta SOS', estadisticas.panicos.tiempoAtencion, 600, 1400, "numero", graficaMostrar, datosMostrar)}
          </>
        )}

      </div>
    </div>
  );
};

export default Estadistica;